/**
 * Events API
 *
 * Functions for interacting with the events endpoints.
 */

import { api } from './client';

// =============================================================================
// Types (re-exported from centralized models)
// =============================================================================

export type {
  EventStatus,
  Event,
  EventRole,
  TimelineEntry,
  EventDocument,
  EventAvailability,
} from '../types/models';

import type { EventStatus, Event, TimelineEntry, EventRole } from '../types/models';

// =============================================================================
// Request/Response Types
// =============================================================================

export interface CreateEventInput {
  name: string;
  venue: string;
  network?: string | null;
  sport?: string | null;
  eventType?: string | null;
  eventTypeId?: string | null;
  eventDate: string;
  callTime?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  tocInfo?: string | null;
  engineeringNotes?: string | null;
  additionalDetails?: string | null;
  status?: EventStatus;
}

export interface UpdateEventInput {
  name?: string;
  venue?: string;
  network?: string | null;
  sport?: string | null;
  eventType?: string | null;
  eventTypeId?: string | null;
  eventDate?: string;
  callTime?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  tocInfo?: string | null;
  engineeringNotes?: string | null;
  additionalDetails?: string | null;
  status?: EventStatus;
  assignmentsConfirmed?: boolean;
}

export interface ListEventsParams {
  status?: EventStatus;
  venue?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListEventsResponse {
  events: Event[];
  /** The org's active crew roster: the denominator for `crew.responded`. */
  activeCrewTotal: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateTimelineEntryInput {
  label: string;
  time: string;
  description?: string | null;
  sortOrder?: number;
}

export interface UpdateTimelineEntryInput {
  label?: string;
  time?: string;
  description?: string | null;
  sortOrder?: number;
}

export interface CreateEventRoleInput {
  roleName: string;
  requiredCount?: number;
  description?: string | null;
  // Payment tracking - rate overrides
  rateOverride?: number | null;
  rateOverrideType?: 'HOURLY' | 'FLAT' | null;
}

export interface UpdateEventRoleInput {
  roleName?: string;
  requiredCount?: number;
  description?: string | null;
  // Payment tracking - rate overrides
  rateOverride?: number | null;
  rateOverrideType?: 'HOURLY' | 'FLAT' | null;
}

export interface BulkEventInput {
  name: string;
  venue: string;
  network?: string | null;
  sport?: string | null;
  eventType?: string | null;
  eventTypeId?: string | null;
  eventDate: string;
  callTime?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  status?: EventStatus;
}

export interface BulkCreateEventsResponse {
  message: string;
  created: number;
  failed: number;
  events: Event[];
  errors: string[];
}

// =============================================================================
// Event CRUD
// =============================================================================

/**
 * List events with optional filters
 */
export async function listEvents(params?: ListEventsParams): Promise<ListEventsResponse> {
  const response = await api.get<ListEventsResponse>('/events', { params });
  return response.data;
}

/**
 * Get a single event by ID
 */
export async function getEvent(id: string): Promise<{ event: Event }> {
  const response = await api.get<{ event: Event }>(`/events/${id}`);
  return response.data;
}

/**
 * Create a new event
 */
export async function createEvent(data: CreateEventInput): Promise<{ message: string; event: Event }> {
  const response = await api.post<{ message: string; event: Event }>('/events', data);
  return response.data;
}

/**
 * Update an event
 */
export async function updateEvent(
  id: string,
  data: UpdateEventInput
): Promise<{ message: string; event: Event }> {
  const response = await api.put<{ message: string; event: Event }>(`/events/${id}`, data);
  return response.data;
}

/**
 * Delete an event
 */
export async function deleteEvent(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/events/${id}`);
  return response.data;
}

/**
 * Confirm assignments for an event
 */
export async function confirmAssignments(id: string): Promise<{ message: string; event: Event }> {
  const response = await api.post<{ message: string; event: Event }>(`/events/${id}/confirm`);
  return response.data;
}

/**
 * Bulk create multiple events
 */
export async function bulkCreateEvents(events: BulkEventInput[]): Promise<BulkCreateEventsResponse> {
  const response = await api.post<BulkCreateEventsResponse>('/events/bulk', { events });
  return response.data;
}

// =============================================================================
// Timeline Entries
// =============================================================================

/**
 * Get timeline entries for an event
 */
export async function getTimelineEntries(eventId: string): Promise<{ entries: TimelineEntry[] }> {
  const response = await api.get<{ entries: TimelineEntry[] }>(`/events/${eventId}/timeline`);
  return response.data;
}

/**
 * Create a timeline entry
 */
export async function createTimelineEntry(
  eventId: string,
  data: CreateTimelineEntryInput
): Promise<{ message: string; entry: TimelineEntry }> {
  const response = await api.post<{ message: string; entry: TimelineEntry }>(
    `/events/${eventId}/timeline`,
    data
  );
  return response.data;
}

/**
 * Update a timeline entry
 */
export async function updateTimelineEntry(
  eventId: string,
  entryId: string,
  data: UpdateTimelineEntryInput
): Promise<{ message: string; entry: TimelineEntry }> {
  const response = await api.put<{ message: string; entry: TimelineEntry }>(
    `/events/${eventId}/timeline/${entryId}`,
    data
  );
  return response.data;
}

/**
 * Delete a timeline entry
 */
export async function deleteTimelineEntry(
  eventId: string,
  entryId: string
): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(
    `/events/${eventId}/timeline/${entryId}`
  );
  return response.data;
}

// =============================================================================
// Event Roles
// =============================================================================

/**
 * Get roles for an event
 */
export async function getEventRoles(eventId: string): Promise<{ roles: EventRole[] }> {
  const response = await api.get<{ roles: EventRole[] }>(`/events/${eventId}/roles`);
  return response.data;
}

/**
 * Create an event role
 */
export async function createEventRole(
  eventId: string,
  data: CreateEventRoleInput
): Promise<{ message: string; role: EventRole }> {
  const response = await api.post<{ message: string; role: EventRole }>(
    `/events/${eventId}/roles`,
    data
  );
  return response.data;
}

/**
 * Update an event role
 */
export async function updateEventRole(
  eventId: string,
  roleId: string,
  data: UpdateEventRoleInput
): Promise<{ message: string; role: EventRole }> {
  const response = await api.put<{ message: string; role: EventRole }>(
    `/events/${eventId}/roles/${roleId}`,
    data
  );
  return response.data;
}

/**
 * Delete an event role
 */
export async function deleteEventRole(
  eventId: string,
  roleId: string
): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(
    `/events/${eventId}/roles/${roleId}`
  );
  return response.data;
}

/**
 * Reorder event roles. `roleIds` must contain every role for the event in the
 * desired display order (lowest index = appears first).
 */
export async function reorderEventRoles(
  eventId: string,
  roleIds: string[]
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(
    `/events/${eventId}/roles/reorder`,
    { roleIds }
  );
  return response.data;
}
